/**
 * R4S OmniMens API Client
 * Handles all communication with the Django backend
 * Includes JWT authentication with automatic token refresh
 */

import axios from 'axios';
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type {
  RestEndpoint,
  Dashboard,
  Widget,
  StatusMapping,
  WidgetTypeInfo,
  DashboardTemplate,
  FieldMapping,
  StatusResult,
  WidgetData,
  Connection,
  MachineCapacityGroup,
  MachineCapacityGroupListItem,
  MachineNumber,
  MachineNumberListItem,
  MachineOperation,
  MachineOperationListItem,
  MachineOperationQuantity,
  MachineStatusSummary,
  CalendarHeader,
  CalendarHeaderListItem,
  CalendarTimeWindow,
  CalendarException,
  WorkCenterCalendarAssignment,
  CalendarAvailability,
  // Phase 8 — Scheduling & Reschedule
  SchedulingRequest,
  SchedulingResult,
  CapacityCheckRequest,
  UtilizationResponse,
  SequenceOptimizeRequest,
  SequenceResult,
  SetupMatrixConfig,
  SetupMatrixResponse,
  HybridPlanningRequest,
  HybridPlanningResult,
  MaterialConstraintsResult,
  SchedulingCacheStatus,
  RescheduleRequest,
  RescheduleResult,
  // Phase 10 — KPI, Site, Cross-Site, Cost
  KPIRequest,
  OEEResponse,
  LeadTimeResponse,
  KPIUtilizationResponse,
  OTDResponse,
  KPISnapshotCreateRequest,
  KPISnapshot,
  KPISnapshotListItem,
  KPITrendResponse,
  Site,
  SiteListItem,
  CrossSiteRequest,
  CrossSiteConflictsResponse,
  MaterialCost,
  MaterialCostListItem,
  CostDashboardRequest,
  ScenarioCostRequest,
  OrderCostSummary,
  // Environment Variables
  EnvironmentVariable,
  EnvironmentVariableListItem,
  EnvironmentVariableCreateRequest,
  EnvironmentVariableUpdateRequest,
  EnvVarStats,
  EnvVarAuditEntry,
  AIConfigSummary,
  AIConfigUpdateRequest,
  ConnectionTestRequest,
  ConnectionTestResult,
  // Email Templates
  EmailTemplate,
  EmailTemplatePreview,
} from '../types';

// API Base URL - configurable via environment variable
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Storage keys
const TOKEN_STORAGE_KEY = 'r4s_omnimens_tokens';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const storedTokens = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (storedTokens) {
      try {
        const tokens = JSON.parse(storedTokens);
        if (tokens.access) {
          config.headers.Authorization = `Bearer ${tokens.access}`;
        }
      } catch {
        // Invalid token storage, ignore
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle 401 errors (token expired)
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const storedTokens = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (storedTokens) {
        try {
          const tokens = JSON.parse(storedTokens);

          // Try to refresh the token
          const refreshResponse = await axios.post(
            `${API_BASE_URL}/auth/refresh/`,
            { refresh: tokens.refresh },
            { headers: { 'Content-Type': 'application/json' } }
          );

          const newTokens = {
            access: refreshResponse.data.access,
            refresh: refreshResponse.data.refresh || tokens.refresh,
          };

          localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(newTokens));

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newTokens.access}`;
          return api(originalRequest);
        } catch {
          // Refresh failed, clear auth and redirect to login
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          localStorage.removeItem('r4s_omnimens_user');
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// Paginated response type from Django REST Framework
interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Helper to extract array from paginated or plain response
function extractResults<T>(data: T[] | PaginatedResponse<T>): T[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && typeof data === 'object' && 'results' in data) {
    return data.results;
  }
  return [];
}

// Error handler
const handleError = (error: AxiosError): never => {
  if (error.response) {
    const data = error.response.data as { error?: string; detail?: string; message?: string };
    const status = error.response.status;
    const errorMessage = data.error || data.detail || data.message;

    if (errorMessage) {
      throw new Error(errorMessage);
    }

    // For 500 errors without specific message, show more context
    if (status >= 500) {
      throw new Error(`Server error (${status}): Please check backend logs for details`);
    }

    throw new Error(`Request failed with status ${status}`);
  }
  throw error;
};

// ============ Endpoints API ============

export const endpointsApi = {
  list: async (): Promise<RestEndpoint[]> => {
    try {
      const response = await api.get('/endpoints/');
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<RestEndpoint> => {
    try {
      const response = await api.get(`/endpoints/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  create: async (data: Partial<RestEndpoint>): Promise<RestEndpoint> => {
    try {
      const response = await api.post('/endpoints/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  update: async (id: string, data: Partial<RestEndpoint>): Promise<RestEndpoint> => {
    try {
      const response = await api.patch(`/endpoints/${id}/`, data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      await api.delete(`/endpoints/${id}/`);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  discover: async (id: string): Promise<unknown[]> => {
    try {
      const response = await api.post(`/endpoints/${id}/discover/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  fetch: async (id: string, params?: Record<string, unknown>): Promise<{ success: boolean; message: string; record_count?: number }> => {
    try {
      const response = await api.post(`/endpoints/${id}/fetch/`, params);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  test: async (id: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.post(`/endpoints/${id}/test/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getMappings: async (id: string): Promise<FieldMapping[]> => {
    try {
      const response = await api.get(`/endpoints/${id}/mappings/`);
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Data API ============

export const dataApi = {
  getData: async (
    endpointId: string,
    params?: { page?: number; page_size?: number }
  ): Promise<{ data: Record<string, unknown>[]; total_count: number; page: number; page_size: number; endpoint_name: string }> => {
    try {
      const response = await api.get(`/data/${endpointId}/`, { params });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getSchema: async (
    endpointId: string
  ): Promise<{ endpoint_id: string; endpoint_name: string; last_updated: string; fields: unknown[] }> => {
    try {
      const response = await api.get(`/data/${endpointId}/schema/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Dashboards API ============

export const dashboardsApi = {
  list: async (): Promise<Dashboard[]> => {
    try {
      const response = await api.get('/dashboards/');
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<Dashboard> => {
    try {
      const response = await api.get(`/dashboards/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  create: async (data: Partial<Dashboard>): Promise<Dashboard> => {
    try {
      const response = await api.post('/dashboards/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  update: async (id: string, data: Partial<Dashboard>): Promise<Dashboard> => {
    try {
      const response = await api.patch(`/dashboards/${id}/`, data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      await api.delete(`/dashboards/${id}/`);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  clone: async (id: string, name?: string): Promise<Dashboard> => {
    try {
      const response = await api.post(`/dashboards/${id}/clone/`, { name });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getWidgets: async (id: string): Promise<Widget[]> => {
    try {
      const response = await api.get(`/dashboards/${id}/widgets/`);
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  addWidget: async (id: string, widget: Partial<Widget>): Promise<Widget> => {
    try {
      const response = await api.post(`/dashboards/${id}/add_widget/`, widget);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Widgets API ============

export const widgetsApi = {
  list: async (dashboardId?: string): Promise<Widget[]> => {
    try {
      const params = dashboardId ? { dashboard: dashboardId } : {};
      const response = await api.get('/widgets/', { params });
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<Widget> => {
    try {
      const response = await api.get(`/widgets/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  update: async (id: string, data: Partial<Widget>): Promise<Widget> => {
    try {
      const response = await api.patch(`/widgets/${id}/`, data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      await api.delete(`/widgets/${id}/`);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getData: async (id: string): Promise<WidgetData> => {
    try {
      const response = await api.get(`/widgets/${id}/data/`);
      // API returns { widget_id, widget_type, data: {...} } - we need the inner data
      return response.data.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Widget Types API ============

export const widgetTypesApi = {
  list: async (): Promise<WidgetTypeInfo[]> => {
    try {
      const response = await api.get('/widget-types/');
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getSchema: async (type: string): Promise<WidgetTypeInfo> => {
    try {
      const response = await api.get(`/widget-types/${type}/schema/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Status Mappings API ============

export const statusMappingsApi = {
  list: async (): Promise<StatusMapping[]> => {
    try {
      const response = await api.get('/status-mappings/');
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: number): Promise<StatusMapping> => {
    try {
      const response = await api.get(`/status-mappings/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  create: async (data: Partial<StatusMapping>): Promise<StatusMapping> => {
    try {
      const response = await api.post('/status-mappings/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  update: async (id: number, data: Partial<StatusMapping>): Promise<StatusMapping> => {
    try {
      const response = await api.patch(`/status-mappings/${id}/`, data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  delete: async (id: number): Promise<void> => {
    try {
      await api.delete(`/status-mappings/${id}/`);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  evaluate: async (id: number, value: unknown, context?: Record<string, unknown>): Promise<StatusResult> => {
    try {
      const response = await api.post(`/status-mappings/${id}/evaluate/`, { value, context });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Connections API ============

export const connectionsApi = {
  list: async (): Promise<Connection[]> => {
    try {
      const response = await api.get('/connections/');
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<Connection> => {
    try {
      const response = await api.get(`/connections/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  create: async (data: Partial<Connection> | Record<string, unknown>): Promise<Connection> => {
    try {
      const response = await api.post('/connections/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  update: async (id: string, data: Partial<Connection> | Record<string, unknown>): Promise<Connection> => {
    try {
      const response = await api.patch(`/connections/${id}/`, data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      await api.delete(`/connections/${id}/`);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  test: async (id: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.post(`/connections/${id}/test/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ OData Service Definitions API ============

export interface ODataServiceDefinition {
  id: string;
  connection: string;
  connection_name: string;
  namespace: string;
  title: string;
  version?: string;
  parsed_entities: Record<string, ODataEntitySchema>;
  entity_count: number;
  uploaded_at: string;
  updated_at: string;
}

export interface ODataEntitySchema {
  entity_set: string;
  entity_type: string;
  properties: ODataProperty[];
  navigation_properties: ODataNavigationProperty[];
  expand_options: string[];
  key_properties: string[];
}

export interface ODataProperty {
  name: string;
  type: string;
  nullable: boolean;
}

export interface ODataNavigationProperty {
  name: string;
  target_entity: string;
  is_collection: boolean;
  multiplicity: '1' | '*';
}

export interface ODataSchemaResponse {
  service_namespace: string | null;
  entity_set: string;
  entity_type: string;
  properties: ODataProperty[];
  navigation_properties: ODataNavigationProperty[];
  expand_options: string[];
  key_properties: string[];
  message?: string;
}

export const odataServicesApi = {
  list: async (connectionId?: string): Promise<ODataServiceDefinition[]> => {
    try {
      const params = connectionId ? { connection_id: connectionId } : {};
      const response = await api.get('/odata-services/', { params });
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<ODataServiceDefinition> => {
    try {
      const response = await api.get(`/odata-services/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  upload: async (connectionId: string, file: File): Promise<{
    id: string;
    namespace: string;
    title: string;
    entity_count: number;
    entities: string[];
    created: boolean;
    message: string;
  }> => {
    try {
      const formData = new FormData();
      formData.append('connection_id', connectionId);
      formData.append('file', file);

      const response = await api.post('/odata-services/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      await api.delete(`/odata-services/${id}/`);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getSchema: async (connectionId: string, endpoint: string): Promise<ODataSchemaResponse> => {
    try {
      const response = await api.get('/odata-services/schema/', {
        params: { connection_id: connectionId, endpoint },
      });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getEntityProperties: async (connectionId: string, entityName: string): Promise<{
    entity: string;
    properties: Array<{ name: string; type: string; nullable: boolean }>;
    found: boolean;
    message?: string;
  }> => {
    try {
      const response = await api.get('/odata-services/entity-properties/', {
        params: { connection_id: connectionId, entity: entityName },
      });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Status Evaluation API ============

export const statusApi = {
  evaluate: async (
    value: unknown,
    options: { mapping_id?: number; mapping_name?: string; context?: Record<string, unknown> }
  ): Promise<StatusResult> => {
    try {
      const response = await api.post('/status/evaluate/', { value, ...options });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getDefaults: async (): Promise<Record<string, StatusMapping>> => {
    try {
      const response = await api.get('/status/defaults/');
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Templates API ============

export const templatesApi = {
  list: async (): Promise<DashboardTemplate[]> => {
    try {
      const response = await api.get('/templates/');
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: number): Promise<DashboardTemplate> => {
    try {
      const response = await api.get(`/templates/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  create: async (data: Partial<DashboardTemplate>): Promise<DashboardTemplate> => {
    try {
      const response = await api.post('/templates/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  update: async (id: number, data: Partial<DashboardTemplate>): Promise<DashboardTemplate> => {
    try {
      const response = await api.patch(`/templates/${id}/`, data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  delete: async (id: number): Promise<void> => {
    try {
      await api.delete(`/templates/${id}/`);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  createDashboard: async (id: number, name?: string): Promise<Dashboard> => {
    try {
      const response = await api.post(`/templates/${id}/create_dashboard/`, { name });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Email Templates API ============

export const emailTemplatesApi = {
  list: async (): Promise<EmailTemplate[]> => {
    try {
      const response = await api.get('/email-templates/');
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<EmailTemplate> => {
    try {
      const response = await api.get(`/email-templates/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  create: async (data: Partial<EmailTemplate>): Promise<EmailTemplate> => {
    try {
      const response = await api.post('/email-templates/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  update: async (id: string, data: Partial<EmailTemplate>): Promise<EmailTemplate> => {
    try {
      const response = await api.patch(`/email-templates/${id}/`, data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      await api.delete(`/email-templates/${id}/`);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  preview: async (id: string, context?: Record<string, string>): Promise<EmailTemplatePreview> => {
    try {
      const response = await api.post(`/email-templates/${id}/preview/`, { context });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  testSend: async (id: string, recipient: string): Promise<{ message: string }> => {
    try {
      const response = await api.post(`/email-templates/${id}/test_send/`, { recipient });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Machine Capacity Groups API (Sprint 7.1 — tirou461) ============

export const machineCapacityGroupsApi = {
  list: async (params?: {
    work_center?: string;
    capacity_type?: string;
    is_active?: boolean;
    search?: string;
    ordering?: string;
  }): Promise<MachineCapacityGroupListItem[]> => {
    try {
      const response = await api.get('/planning/machine-capacity-groups/', { params });
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<MachineCapacityGroup> => {
    try {
      const response = await api.get(`/planning/machine-capacity-groups/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getMachines: async (id: string): Promise<MachineNumberListItem[]> => {
    try {
      const response = await api.get(`/planning/machine-capacity-groups/${id}/machines/`);
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Machines API (Sprint 7.1 — tirou462) ============

export const machinesApi = {
  list: async (params?: {
    capacity_group?: string;
    work_center?: string;
    status?: string;
    is_active?: boolean;
    search?: string;
    ordering?: string;
  }): Promise<MachineNumberListItem[]> => {
    try {
      const response = await api.get('/planning/machines/', { params });
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<MachineNumber> => {
    try {
      const response = await api.get(`/planning/machines/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getOperations: async (id: string): Promise<MachineOperationListItem[]> => {
    try {
      const response = await api.get(`/planning/machines/${id}/operations/`);
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getStatusSummary: async (): Promise<MachineStatusSummary[]> => {
    try {
      const response = await api.get('/planning/machines/status-summary/');
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Machine Operations API (Sprint 7.1 — tisfc400) ============

export const machineOperationsApi = {
  list: async (params?: {
    operation?: string;
    machine?: string;
    is_active?: boolean;
    erp_order_number?: string;
    search?: string;
    ordering?: string;
  }): Promise<MachineOperationListItem[]> => {
    try {
      const response = await api.get('/planning/machine-operations/', { params });
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<MachineOperation> => {
    try {
      const response = await api.get(`/planning/machine-operations/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getQuantities: async (id: string): Promise<MachineOperationQuantity[]> => {
    try {
      const response = await api.get(`/planning/machine-operations/${id}/quantities/`);
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Machine Operation Quantities API (Sprint 7.1 — tisfc401) ============

export const machineOperationQuantitiesApi = {
  list: async (params?: {
    machine_operation?: string;
    quantity_type?: string;
    ordering?: string;
  }): Promise<MachineOperationQuantity[]> => {
    try {
      const response = await api.get('/planning/machine-operation-quantities/', { params });
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<MachineOperationQuantity> => {
    try {
      const response = await api.get(`/planning/machine-operation-quantities/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Calendar Headers API (Sprint 7.2 — tccom020) ============

export const calendarHeadersApi = {
  list: async (params?: {
    calendar_type?: string;
    is_active?: boolean;
    search?: string;
    ordering?: string;
  }): Promise<CalendarHeaderListItem[]> => {
    try {
      const response = await api.get('/planning/calendar-headers/', { params });
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<CalendarHeader> => {
    try {
      const response = await api.get(`/planning/calendar-headers/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getTimeWindows: async (id: string): Promise<CalendarTimeWindow[]> => {
    try {
      const response = await api.get(`/planning/calendar-headers/${id}/time-windows/`);
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getExceptions: async (id: string): Promise<CalendarException[]> => {
    try {
      const response = await api.get(`/planning/calendar-headers/${id}/exceptions/`);
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getWorkCenters: async (id: string): Promise<WorkCenterCalendarAssignment[]> => {
    try {
      const response = await api.get(`/planning/calendar-headers/${id}/work-centers/`);
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getAvailability: async (id: string, date: string): Promise<CalendarAvailability> => {
    try {
      const response = await api.get(`/planning/calendar-headers/${id}/availability/`, {
        params: { date },
      });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Calendar Time Windows API (Sprint 7.2 — tccom025) ============

export const calendarTimeWindowsApi = {
  list: async (params?: {
    calendar?: string;
    day_of_week?: number;
    is_active?: boolean;
    ordering?: string;
  }): Promise<CalendarTimeWindow[]> => {
    try {
      const response = await api.get('/planning/calendar-time-windows/', { params });
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<CalendarTimeWindow> => {
    try {
      const response = await api.get(`/planning/calendar-time-windows/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Calendar Exceptions API (Sprint 7.2 — tccom030) ============

export const calendarExceptionsApi = {
  list: async (params?: {
    calendar?: string;
    exception_type?: string;
    is_recurring?: boolean;
    is_active?: boolean;
    search?: string;
    ordering?: string;
  }): Promise<CalendarException[]> => {
    try {
      const response = await api.get('/planning/calendar-exceptions/', { params });
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<CalendarException> => {
    try {
      const response = await api.get(`/planning/calendar-exceptions/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Work Center Calendar Assignments API (Sprint 7.2 — tirou010) ============

export const wcCalendarAssignmentsApi = {
  list: async (params?: {
    work_center?: string;
    calendar?: string;
    is_primary?: boolean;
    is_active?: boolean;
    ordering?: string;
  }): Promise<WorkCenterCalendarAssignment[]> => {
    try {
      const response = await api.get('/planning/wc-calendar-assignments/', { params });
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<WorkCenterCalendarAssignment> => {
    try {
      const response = await api.get(`/planning/wc-calendar-assignments/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Scheduling API (Phase 8 — APS Engine) ============

export const schedulingApi = {
  backward: async (data: SchedulingRequest): Promise<SchedulingResult> => {
    try {
      const response = await api.post('/planning/scheduling/backward/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  forward: async (data: SchedulingRequest): Promise<SchedulingResult> => {
    try {
      const response = await api.post('/planning/scheduling/forward/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  capacityCheck: async (data: CapacityCheckRequest): Promise<SchedulingResult> => {
    try {
      const response = await api.post('/planning/scheduling/capacity-check/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  utilization: async (data: CapacityCheckRequest): Promise<UtilizationResponse> => {
    try {
      const response = await api.post('/planning/scheduling/utilization/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  sequenceOptimize: async (data: SequenceOptimizeRequest): Promise<SequenceResult> => {
    try {
      const response = await api.post('/planning/scheduling/sequence-optimize/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  setupMatrices: async (data: SetupMatrixConfig[]): Promise<SetupMatrixResponse> => {
    try {
      const response = await api.post('/planning/scheduling/setup-matrices/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  hybrid: async (data: HybridPlanningRequest): Promise<HybridPlanningResult> => {
    try {
      const response = await api.post('/planning/scheduling/hybrid/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  materialConstraints: async (data: SchedulingRequest): Promise<MaterialConstraintsResult> => {
    try {
      const response = await api.post('/planning/scheduling/material-constraints/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getCacheStatus: async (): Promise<SchedulingCacheStatus> => {
    try {
      const response = await api.get('/planning/scheduling/cache-status/');
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  invalidateCache: async (): Promise<{ message: string }> => {
    try {
      const response = await api.post('/planning/scheduling/cache-invalidate/');
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Reschedule API (Phase 8 — D&D Gantt) ============

export const rescheduleApi = {
  reschedule: async (operationId: string, data: RescheduleRequest): Promise<RescheduleResult> => {
    try {
      const response = await api.patch(`/planning/reschedule/${operationId}/`, data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  preview: async (operationId: string, data: RescheduleRequest): Promise<RescheduleResult> => {
    try {
      const response = await api.post(`/planning/reschedule/${operationId}/preview/`, data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ KPI API (Phase 10 — KPI Dashboard) ============

export const kpiApi = {
  oee: async (data: KPIRequest): Promise<OEEResponse> => {
    try {
      const response = await api.post('/planning/kpi/oee/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  leadTime: async (data: KPIRequest): Promise<LeadTimeResponse> => {
    try {
      const response = await api.post('/planning/kpi/lead-time/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  utilization: async (data: KPIRequest): Promise<KPIUtilizationResponse> => {
    try {
      const response = await api.post('/planning/kpi/utilization/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  otd: async (data: KPIRequest): Promise<OTDResponse> => {
    try {
      const response = await api.post('/planning/kpi/otd/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  createSnapshot: async (data: KPISnapshotCreateRequest): Promise<KPISnapshot> => {
    try {
      const response = await api.post('/planning/kpi/snapshot/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getTrend: async (params: {
    metric: string;
    days?: number;
    snapshot_type?: string;
    site_code?: string;
  }): Promise<KPITrendResponse> => {
    try {
      const response = await api.get('/planning/kpi/trend/', { params });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  getSnapshots: async (): Promise<KPISnapshotListItem[]> => {
    try {
      const response = await api.get('/planning/kpi/snapshots/');
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Sites API (Phase 10 — Multi-Site) ============

export const sitesApi = {
  list: async (): Promise<SiteListItem[]> => {
    try {
      const response = await api.get('/planning/sites/');
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<Site> => {
    try {
      const response = await api.get(`/planning/sites/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  create: async (data: Partial<Site>): Promise<Site> => {
    try {
      const response = await api.post('/planning/sites/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  update: async (id: string, data: Partial<Site>): Promise<Site> => {
    try {
      const response = await api.patch(`/planning/sites/${id}/`, data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      await api.delete(`/planning/sites/${id}/`);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Cross-Site API (Phase 10) ============

export const crossSiteApi = {
  consolidated: async (data: CrossSiteRequest): Promise<Record<string, unknown>> => {
    try {
      const response = await api.post('/planning/cross-site/consolidated/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  conflicts: async (data: CrossSiteRequest): Promise<CrossSiteConflictsResponse> => {
    try {
      const response = await api.post('/planning/cross-site/conflicts/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Material Costs API (Phase 10) ============

export const materialCostsApi = {
  list: async (): Promise<MaterialCostListItem[]> => {
    try {
      const response = await api.get('/planning/material-costs/');
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<MaterialCost> => {
    try {
      const response = await api.get(`/planning/material-costs/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  dashboard: async (data: CostDashboardRequest): Promise<Record<string, unknown>> => {
    try {
      const response = await api.post('/planning/material-costs/dashboard/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  scenarioForecast: async (data: ScenarioCostRequest): Promise<Record<string, unknown>> => {
    try {
      const response = await api.post('/planning/material-costs/scenario-forecast/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  byOrder: async (orderId: string): Promise<OrderCostSummary> => {
    try {
      const response = await api.get(`/planning/material-costs/by-order/${orderId}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============ Environment Variables API ============

export const environmentApi = {
  list: async (params?: {
    category?: string;
    scope?: string;
    is_sensitive?: boolean;
    search?: string;
  }): Promise<EnvironmentVariableListItem[]> => {
    try {
      const response = await api.get('/planning/environment-variables/', { params });
      const items = extractResults(response.data);
      // Backend returns the (masked) value as `value`; map to `masked_value`
      // so the rest of the frontend can use either field name.
      return items.map((item: any) => ({
        ...item,
        masked_value: item.masked_value ?? item.value ?? '',
      }));
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<EnvironmentVariable> => {
    try {
      const response = await api.get(`/planning/environment-variables/${id}/`);
      const data = response.data;
      return { ...data, masked_value: data.masked_value ?? data.value ?? '' };
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  create: async (data: EnvironmentVariableCreateRequest): Promise<EnvironmentVariable> => {
    try {
      const response = await api.post('/planning/environment-variables/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  update: async (id: string, data: EnvironmentVariableUpdateRequest): Promise<EnvironmentVariable> => {
    try {
      const response = await api.patch(`/planning/environment-variables/${id}/`, data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      await api.delete(`/planning/environment-variables/${id}/`);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  stats: async (): Promise<EnvVarStats> => {
    try {
      const response = await api.get('/planning/environment-variables/stats/');
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  audit: async (id: string): Promise<EnvVarAuditEntry[]> => {
    try {
      const response = await api.get(`/planning/environment-variables/${id}/audit/`);
      return extractResults(response.data);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  reveal: async (id: string): Promise<{ value: string }> => {
    try {
      const response = await api.post(`/planning/environment-variables/${id}/reveal/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  aiConfig: async (): Promise<AIConfigSummary> => {
    try {
      const response = await api.get('/planning/environment-variables/ai-config/');
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  updateAIConfig: async (data: AIConfigUpdateRequest): Promise<AIConfigSummary> => {
    try {
      const response = await api.patch('/planning/environment-variables/ai-config/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  testConnection: async (data: ConnectionTestRequest): Promise<ConnectionTestResult> => {
    try {
      const response = await api.post('/planning/environment-variables/test-connection/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============================================================================
// Sync Service Configuration API
// ============================================================================

export interface SyncServiceConfig {
  id: string;
  connection: string;
  connection_name: string;
  entity_type: string;
  entity_type_display: string;
  is_enabled: boolean;
  odata_path: string;
  batch_size: number;
  delta_sync_enabled: boolean;
  sync_interval_minutes: number;
  last_synced_at: string | null;
  last_sync_status: string;
  last_sync_records: number;
  last_error: string;
  total_syncs: number;
  status_label: string;
  created_at: string;
  updated_at: string;
}

export interface AvailableSyncService {
  entity_type: string;
  entity_type_display: string;
  odata_path: string;
  batch_size: number;
  description: string;
}

export const syncServicesApi = {
  list: async (connectionId?: string): Promise<SyncServiceConfig[]> => {
    try {
      const params = connectionId ? { connection: connectionId } : {};
      const response = await api.get('/planning/sync-services/', { params });
      return response.data.results || response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  get: async (id: string): Promise<SyncServiceConfig> => {
    try {
      const response = await api.get(`/planning/sync-services/${id}/`);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  create: async (data: Partial<SyncServiceConfig>): Promise<SyncServiceConfig> => {
    try {
      const response = await api.post('/planning/sync-services/', data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  update: async (id: string, data: Partial<SyncServiceConfig>): Promise<SyncServiceConfig> => {
    try {
      const response = await api.patch(`/planning/sync-services/${id}/`, data);
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      await api.delete(`/planning/sync-services/${id}/`);
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  available: async (): Promise<{ count: number; services: AvailableSyncService[] }> => {
    try {
      const response = await api.get('/planning/sync-services/available/');
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  register: async (connectionId: string, entityTypes: string[]): Promise<{
    created_count: number;
    skipped_count: number;
    created: string[];
    skipped: string[];
  }> => {
    try {
      const response = await api.post('/planning/sync-services/register/', {
        connection_id: connectionId,
        entity_types: entityTypes,
      });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  syncNow: async (id: string, delta: boolean = false): Promise<{
    status: string;
    task_id: string;
    entity_type: string;
    delta: boolean;
  }> => {
    try {
      const response = await api.post(`/planning/sync-services/${id}/sync-now/`, { delta });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  viewData: async (id: string, params?: { page?: number; page_size?: number }): Promise<{
    entity_type: string;
    entity_type_display: string;
    connection_name: string;
    data: Record<string, unknown>[];
    total_count: number;
    page: number;
    page_size: number;
  }> => {
    try {
      const response = await api.get(`/planning/sync-services/${id}/view-data/`, { params });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// ============================================================================
// Docker Management API
// ============================================================================

export const dockerApi = {
  /** Get list of allowed services */
  getInfo: async (): Promise<{ allowed_services: string[]; usage: string }> => {
    try {
      const response = await api.get('/admin/docker-restart/');
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },

  /** Restart Docker services */
  restart: async (services: string[]): Promise<{
    status: string;
    task_id: string;
    services: string[];
    triggered_by: string;
    message: string;
  }> => {
    try {
      const response = await api.post('/admin/docker-restart/', { services });
      return response.data;
    } catch (error) {
      return handleError(error as AxiosError);
    }
  },
};

// Export default api instance for custom requests
export default api;
